import type { CityConfig } from "../city-template/types";

export const grandRapidsConfig: CityConfig = {
  city: "Grand Rapids",
  state: "Michigan",
  stateAbbr: "MI",
  slug: "grand-rapids",
  route: "/cities/grand-rapids",
  title: "Emergency Passport Grand Rapids MI | Expedited Passport Processing | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Grand Rapids, Michigan — as fast as 24 hours. Fast passport renewal and new passport applications for Grand Rapids, Wyoming, Kentwood, Walker, Grandville, and West Michigan residents. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Grand Rapids, MI",
  heroSubtitle: "Grand Rapids travelers — from furniture industry executives to West Michigan families to Calvin and GVSU students — trust Fast Passport Center for emergency and expedited passports as fast as 24 hours. A+ BBB rated courier service serving all of Kent County and West Michigan.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Scenic%20view%20of%20Grand%20Rapids%20Michigan%20downtown%20skyline%20along%20the%20Grand%20River%20with%20modern%20bridges%2C%20DeVos%20Place%20convention%20center%2C%20colorful%20buildings%20reflected%20in%20the%20calm%20river%20water%2C%20vibrant%20West%20Michigan%20city%20atmosphere%2C%20warm%20afternoon%20sunlight%2C%20lush%20green%20riverbanks%2C%20professional%20architectural%20photography%20without%20text%20or%20people&width=1800&height=700&seq=grand-rapids-hero-002&orientation=landscape",
  heroImageAlt: "Grand Rapids Michigan downtown skyline along the Grand River with bridges and DeVos Place",
  geoPosition: "42.9634;-85.6681",
  geoPlacename: "Grand Rapids, Michigan",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Detroit Passport Agency",
    address: "211 W. Fort St, Suite 1300, Detroit, MI 48226",
    distance: "Approximately 2.5–3 hours east of Grand Rapids via I-96",
  },
  nearbyCitiesZips: [
    { name: "Grand Rapids", zips: "49501, 49503, 49504, 49505, 49506, 49507, 49508, 49509, 49510, 49512, 49515, 49516, 49518, 49519, 49525, 49534, 49544, 49546, 49548" },
    { name: "Wyoming", zips: "49418, 49419, 49509" },
    { name: "Kentwood", zips: "49508, 49512, 49548" },
    { name: "Walker", zips: "49534" },
    { name: "Grandville", zips: "49418" },
    { name: "East Grand Rapids", zips: "49506" },
    { name: "Caledonia", zips: "49316" },
    { name: "Byron Center", zips: "49315" },
    { name: "Jenison", zips: "49428, 49429" },
    { name: "Holland", zips: "49422, 49423, 49424" },
    { name: "Rockford", zips: "49341" },
    { name: "Cedar Springs", zips: "49319" },
    { name: "Ada", zips: "49301" },
    { name: "Lowell", zips: "49331" },
    { name: "Hudsonville", zips: "49426" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier to Detroit or Chicago agencies — no 3-hour drive required" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-building-line", title: "Business Travel Hub", desc: "Serving furniture industry, healthcare, and corporate travel needs" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no road trip to Detroit or Chicago" },
  ],
  internalLinks: [
    { name: "Expedited passport in Wyoming, MI", route: "/states/michigan" },
    { name: "Emergency passport help in Holland, MI", route: "/states/michigan" },
    { name: "Urgent passport services in Kalamazoo, MI", route: "/states/michigan" },
    { name: "Michigan passport services overview", route: "/states/michigan" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Grand Rapids, Michigan remotely via secure registered courier. Our registered couriers hand-deliver your documents to the Detroit Passport Agency (2.5–3 hours east) or Chicago Passport Agency (3 hours south) — without you making the drive. Grand Rapids is equidistant from both agencies, and we always choose the fastest available appointment.",
  fpcOfficeDistance: "Serving Grand Rapids, Wyoming, Kentwood, Walker, Grandville, East Grand Rapids, Holland, Rockford, Jenison, and all of West Michigan",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Grand Rapids and West Michigan. From the thriving downtown along the Grand River to the furniture corridor in Caledonia and Grandville, from the college campuses of Calvin University and Grand Valley State to the Lake Michigan shore communities — we serve all of Kent County and beyond.",
  nearbyCitiesIntro: "We serve Grand Rapids and all nearby communities including Wyoming, Kentwood, Walker, Grandville, East Grand Rapids, Caledonia, Byron Center, Jenison, Holland, Rockford, Cedar Springs, Ada, Lowell, and Hudsonville. We also serve Ottawa County, Allegan County, and western Michigan communities.",
  howItWorksIntro: "Most Grand Rapids travelers complete everything online and ship documents overnight. No need to drive 2.5–3 hours to Detroit or 3 hours to Chicago — our couriers handle both agencies and always pick the fastest route.",
  mistakesIntro: "Grand Rapids is a growing international business hub and major college town. Here are the most common passport mistakes we see from West Michigan travelers.",
  testimonialsIntro: "Grand Rapids travelers — from furniture executives to college families to Lake Michigan vacation planners — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Grand Rapids, MI? Here are the most common ones we hear from West Michigan travelers.",
  statePageRoute: "/states/michigan",
  statePageName: "Michigan",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 2.5–3 hours to Detroit or Chicago", "Full day commitment — difficult from Grand Rapids"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Detroit or Chicago agency — fastest option chosen", "No long drive required", "Perfect for business and family travel deadlines", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Detroit or Chicago agency — fastest option chosen", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Grand Rapids travelers with immediate departure needs — as fast as 24 hours. Our couriers use the Detroit or Chicago agency — whichever is fastest. Perfect for last-minute business trips and family emergencies.",
      bullets: ["24–48 hour turnaround available", "Detroit or Chicago agency — fastest route chosen", "West Michigan business and family travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Grand Rapids adults with a passport issued within the last 15 years.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to your West Michigan address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Grand Rapids residents and families — including Calvin and GVSU students, new citizens, and West Michigan families.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Grand Rapids residents who lost their passport before international travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Grand Rapids and West Michigan families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Grand Rapids and West Michigan residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Grand Rapids MI", description: "Urgent passport processing for Grand Rapids Michigan travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Grand Rapids MI", description: "Fast passport renewal for Grand Rapids Michigan residents with a previously-issued passport." },
    { name: "First-Time Passport Grand Rapids MI", description: "New passport application services for Grand Rapids Michigan residents applying for their first passport." },
    { name: "Child Passport Grand Rapids MI", description: "Minor passport services for Grand Rapids Michigan families with children under 16." },
  ],
  testimonials: [
    {
      name: "Robert H.",
      location: "Grand Rapids, MI",
      rating: 5,
      text: "Furniture market trip to Europe and my passport had less than 6 months validity. Fast Passport Center had a renewed passport in 2 days. The whole process was done through their website — no trips anywhere. Absolute lifesaver.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "Sarah V.",
      location: "Kentwood, MI",
      rating: 5,
      text: "Family vacation to Mexico with 10 days notice. Three passports — two adult renewals and a first-time for our daughter. Done in 6 days, all shipped from the UPS Store near Woodland Mall. Amazing service.",
      service: "Family Passport Bundle",
    },
    {
      name: "Pastor Mark T.",
      location: "Wyoming, MI",
      rating: 5,
      text: "Mission trip to Central America with 12 days notice. Four passports for our team, all first-time applications. Fast Passport Center walked us through every step. All four arrived in time. Truly exceptional.",
      service: "First-Time Passports",
    },
    {
      name: "Lisa B.",
      location: "East Grand Rapids, MI",
      rating: 5,
      text: "Lost my passport the week before a Caribbean cruise departing from Miami. Fast Passport Center replaced it in 48 hours. I made my cruise. Will never use anyone else.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "gr-1",
      name: "Grand Rapids Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "225 Michigan St NW",
      city: "Grand Rapids",
      state: "MI",
      zip: "49503",
      phone: "(616) 776-1600",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Grand Rapids, near DeVos Place",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary downtown Grand Rapids acceptance facility. Appointments required — book 1–2 weeks in advance. Convenient to downtown office workers and medical district.",
      icon: "ri-mail-line",
    },
    {
      id: "gr-2",
      name: "Wyoming Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2070 44th St SW",
      city: "Wyoming",
      state: "MI",
      zip: "49519",
      phone: "(616) 534-5620",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Wyoming, approximately 5 miles south of downtown Grand Rapids",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Wyoming, Grandville, and Southwest Grand Rapids residents. Ample parking available.",
      icon: "ri-mail-line",
    },
    {
      id: "gr-3",
      name: "Kentwood Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4350 Breton Rd SE",
      city: "Kentwood",
      state: "MI",
      zip: "49508",
      phone: "(616) 452-1630",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Kentwood, approximately 5 miles southeast of downtown Grand Rapids",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Kentwood, Caledonia, and East Grand Rapids residents. Good alternative when downtown is fully booked.",
      icon: "ri-mail-line",
    },
    {
      id: "gr-4",
      name: "Walker Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4300 Roger B Chaffee Memorial Blvd NW",
      city: "Walker",
      state: "MI",
      zip: "49534",
      phone: "(616) 791-2600",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Walker, approximately 7 miles northwest of downtown Grand Rapids",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Walker, northwest Grand Rapids, and Standale communities. Ample parking available.",
      icon: "ri-mail-line",
    },
    {
      id: "gr-5",
      name: "Grand Rapids City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "300 Monroe Ave NW",
      city: "Grand Rapids",
      state: "MI",
      zip: "49503",
      phone: "(616) 456-3010",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "Downtown Grand Rapids City Hall",
      website: "https://www.grandrapidsmi.gov/",
      websiteLabel: "City of Grand Rapids",
      notes: "City Hall passport acceptance. Extended hours compared to post office locations. Call to confirm current passport acceptance hours.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, 2–3 week wait", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 2.5–3 hour drive to Detroit or Chicago", fpc: "No — ship from Grand Rapids" },
    { feature: "Agency Selection", agency: "Must choose one yourself", fpc: "We pick Detroit or Chicago — fastest available" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Grand Rapids furniture industry professionals and family travelers often discover passport issues just before departure. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Passport Photo",
      description: "Local photo services near Woodland Mall or 28th Street sometimes produce photos that meet ID requirements but fail passport standards. Photos must be exactly 2x2 inches on a white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at Kent County acceptance facilities.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Not Booking Acceptance Facilities Early Enough",
      description: "Grand Rapids Post Office appointments fill during summer and winter holiday travel seasons. Book 2–3 weeks ahead.",
    },
    {
      icon: "ri-road-map-line",
      title: "Underestimating the Drive to Detroit",
      description: "Many Grand Rapids residents attempt to drive to the Detroit Passport Agency in an emergency. The 2.5–3 hour drive each way means a full day — our courier service is faster and more reliable.",
    },
    {
      icon: "ri-time-line",
      title: "Assuming 4–6 Weeks Is Enough",
      description: "Routine processing takes 6–13 weeks. If your travel is within 10 weeks, use expedited service. Winter Caribbean travel from GRR peaks January–March — plan ahead.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from anywhere in the Grand Rapids area or West Michigan." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx near downtown Grand Rapids or Woodland Mall." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Our registered courier submits to Detroit or Chicago Passport Agency — whichever is fastest for you." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Grand Rapids address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Grand Rapids, MI?", a: "Fast Passport Center offers emergency passport processing in Grand Rapids, Michigan in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Detroit Passport Agency (2.5–3 hours east) or Chicago Passport Agency (3 hours south) — without you making the drive." },
    { q: "Does Grand Rapids have its own passport agency?", a: "No — Grand Rapids does not have a regional passport agency. The nearest facilities are the Detroit Passport Agency (approximately 2.5–3 hours east via I-96) and the Chicago Passport Agency (approximately 3 hours south via I-196 and I-94). Fast Passport Center's courier service means Grand Rapids residents never need to make either drive." },
    { q: "Can I get an expedited passport in Wyoming, Kentwood, or Holland?", a: "Absolutely. Fast Passport Center serves all of West Michigan including Wyoming, Kentwood, Walker, Grandville, East Grand Rapids, Holland, Rockford, Jenison, Byron Center, and all of Kent and Ottawa counties. Our service works the same way everywhere." },
    { q: "What airport do Grand Rapids travelers use for international flights?", a: "Gerald R. Ford International Airport (GRR) is the primary airport for Grand Rapids and West Michigan. GRR offers direct flights to Atlanta, Chicago, Dallas, Orlando, and select seasonal international routes. Most international flights connect through Chicago O'Hare (ORD), Detroit Metro (DTW), or Atlanta (ATL). Always verify passport validity for at least 6 months past your return date." },
    { q: "Can I use Chicago's passport agency from Grand Rapids?", a: "Yes. The Chicago Passport Agency is approximately 3 hours south of Grand Rapids via I-196 and I-94. For some emergency cases, it may offer earlier appointments than Detroit. Fast Passport Center's couriers serve both agencies — we always choose the fastest option for your timeline." },
    { q: "How much does an expedited passport cost in Grand Rapids, MI?", a: "Government fees are fixed: $130 for a first-time adult passport book or $130 + $60 expedite fee for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your specific departure timeline." },
    { q: "Where can I get passport photos in Grand Rapids?", a: "Walgreens, CVS, and Meijer stores throughout Grand Rapids offer passport photos. UPS Store and FedEx Office locations also provide them. Meijer is a particularly convenient option in West Michigan — most locations have photo centers. Photos must be 2x2 inches on a white background, taken within 6 months." },
    { q: "Does Fast Passport Center serve Holland and Kalamazoo?", a: "Yes. Fast Passport Center serves all of West Michigan including Holland (Ottawa County), Kalamazoo, Portage, Battle Creek, Muskegon, and all surrounding communities. Our courier service reaches every corner of Western Michigan." },
  ],
  photoLocations: [
    { name: "Meijer — 28th Street Grand Rapids", address: "3825 28th St SE, Grand Rapids, MI 49512", hours: "Mon–Sun: 6AM–Midnight" },
    { name: "Walgreens — Division Ave Grand Rapids", address: "2245 Division Ave S, Grand Rapids, MI 49507", hours: "Mon–Sun: 7AM–10PM" },
    { name: "UPS Store — Woodland Mall Area", address: "3600 28th St SE, Grand Rapids, MI 49512", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "FedEx Office — Beltline NE", address: "4570 Plainfield Ave NE, Grand Rapids, MI 49525", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    { name: "Michigan SOS — Grand Rapids", address: "925 Wealthy St SE, Grand Rapids, MI 49506", hours: "Mon–Fri: 9:00AM–5:00PM", note: "Michigan Secretary of State for driver's licenses, REAL ID, and vehicle registration. Online reservations strongly recommended.", featured: true },
    { name: "Michigan SOS — Wyoming", address: "1474 Gezon Pkwy SW, Wyoming, MI 49509", hours: "Mon–Fri: 9:00AM–5:00PM", note: "Serves Wyoming, Grandville, and south Grand Rapids. Full REAL ID processing available.", featured: false },
  ],
  airports: [
    {
      code: "GRR",
      name: "Gerald R. Ford International Airport",
      distance: "12 miles southeast of downtown Grand Rapids via I-96",
      address: "5500 44th St SE, Grand Rapids, MI 49512",
      note: "Primary airport for Grand Rapids and West Michigan. Direct flights to Atlanta, Chicago, Dallas, Orlando, and seasonal Caribbean destinations. Most international travel connects through Atlanta (ATL), Chicago O'Hare (ORD), or Detroit (DTW).",
      flightUrl: "https://www.grr.org/",
      flightLabel: "GRR Flight Info",
      featured: true,
    },
    {
      code: "DTW",
      name: "Detroit Metro Wayne County Airport",
      distance: "Approximately 2.5–3 hours east of Grand Rapids via I-96",
      address: "1 Detroit Metro Airport, Romulus, MI 48242",
      note: "Larger international hub for Grand Rapids travelers needing direct international flights. Delta hub with direct routes to Europe, Asia, and Latin America.",
      flightUrl: "https://www.metroairport.com/",
      flightLabel: "DTW Flight Info",
      featured: false,
    },
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "Approximately 3 hours south of Grand Rapids via I-196 and I-94",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Major hub for West Michigan travelers seeking more international routes. United and American Airlines hub with global connections.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "ORD Flight Info",
      featured: false,
    },
  ],
};